import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { InboxChatModal } from '~/features/chat/inbox-chat-modal';
import { documentToFeedItem, newChatAsset, sessionToModalAsset } from '~/features/chat/modal-asset';
import { CodingSessionModal } from '~/features/coding/coding-session-modal';
import { chatKeys } from '~/lib/query-keys';
import { useChatOpenStore } from '~/lib/stores/chat-open-store';
import { useCodingOpenStore } from '~/lib/stores/coding-open-store';
import { shouldCloseOnPageChange, useOverlayOpenStore } from './overlay-open-store';

/**
 * The host for the chat, document and Build overlays, mounted once in the app
 * shell (`layouts/app-layout.tsx`, inside `ChatProvider`). It consumes the
 * three one-shot open stores and the overlay store (`overlay-open-store.ts`)
 * and renders ONE full-screen overlay at a time.
 *
 * Rules:
 * - one overlay at a time (`overlay-open-store.ts` clears the other on open);
 * - a close navigates back to the page the request came from (FRONT-1550),
 *   `replace: true` so the Home detour leaves no history entry;
 * - the "+" inside the chat window starts a fresh chat in place, keeping the
 *   origin of the window it replaces — on a chat only; a briefing item or a
 *   document opened from the feed has no "+";
 * - the overlays live on Home, so leaving for another page (Back, the
 *   sidebar, the command palette) closes them in place;
 * - the Pro Builder window opening over a chat closes it in place (inside
 *   `InboxChatModal`).
 */
export function OverlayHost() {
  useConsumeOpenRequests();
  useConsumeNavigation();
  const chat = useOverlayOpenStore((s) => s.chat);
  const build = useOverlayOpenStore((s) => s.build);
  useCloseOnLeavingPage(chat !== null || build !== null);
  return (
    <>
      {chat && <OverlayChat key={chat.item.id} />}
      {build && <OverlayBuild />}
    </>
  );
}

/** The three one-shot stores' requests, consumed and cleared the instant they are read. */
function useConsumeOpenRequests(): void {
  const chatRequest = useChatOpenStore((s) => s.openRequest);
  const clearChat = useChatOpenStore((s) => s.clearOpenChat);
  useEffect(() => {
    if (!chatRequest) return;
    useOverlayOpenStore
      .getState()
      .openChat(
        sessionToModalAsset(chatRequest.sessionId, chatRequest.title, new Date().toISOString(), chatRequest.directAgent),
        chatRequest.returnTo,
      );
    clearChat();
  }, [chatRequest, clearChat]);

  const docRequest = useChatOpenStore((s) => s.openDocumentRequest);
  const clearDoc = useChatOpenStore((s) => s.clearOpenDocument);
  useEffect(() => {
    if (!docRequest) return;
    useOverlayOpenStore.getState().openChat(documentToFeedItem(docRequest.doc), docRequest.returnTo);
    clearDoc();
  }, [docRequest, clearDoc]);

  const codingRequest = useCodingOpenStore((s) => s.openRequest);
  const clearCoding = useCodingOpenStore((s) => s.clearOpenCoding);
  useEffect(() => {
    if (!codingRequest) return;
    // The window resumes the latest build when no session is named.
    useOverlayOpenStore.getState().openBuild(codingRequest.sessionId ?? undefined, codingRequest.returnTo);
    clearCoding();
  }, [codingRequest, clearCoding]);
}

function useCloseOnLeavingPage(open: boolean): void {
  const { pathname } = useLocation();
  const previous = useRef(pathname);
  useEffect(() => {
    const from = previous.current;
    previous.current = pathname;
    if (!shouldCloseOnPageChange(open, from, pathname)) return;
    const s = useOverlayOpenStore.getState();
    s.clearChat();
    s.clearBuild();
  }, [open, pathname]);
}

/** A store function that wanted a route (Settings from a plain function) gets it here, inside the router. */
function useConsumeNavigation(): void {
  const navigate = useNavigate();
  const target = useOverlayOpenStore((s) => s.navigateTo);
  useEffect(() => {
    if (!target) return;
    useOverlayOpenStore.getState().clearNavigation();
    navigate(target);
  }, [target, navigate]);
}

function useReturnToOrigin() {
  const navigate = useNavigate();
  return useCallback(
    (returnTo: string | null) => {
      if (returnTo !== null) navigate(returnTo, { replace: true });
    },
    [navigate],
  );
}

function OverlayChat() {
  const qc = useQueryClient();
  const chat = useOverlayOpenStore((s) => s.chat);
  const returnToOrigin = useReturnToOrigin();
  const refreshThreads = useCallback(() => qc.invalidateQueries({ queryKey: chatKeys.threadsRoot() }), [qc]);

  const closeInPlace = useCallback(() => {
    useOverlayOpenStore.getState().clearChat();
    refreshThreads();
  }, [refreshThreads]);
  const close = useCallback(() => {
    const target = useOverlayOpenStore.getState().chat?.returnTo ?? null;
    closeInPlace();
    returnToOrigin(target);
  }, [closeInPlace, returnToOrigin]);
  const startBlankChat = useCallback(() => {
    const s = useOverlayOpenStore.getState();
    s.openChat(newChatAsset(), s.chat?.returnTo ?? null);
    // The thread row is minted server-side on the first message; nudge the thread queries.
    window.setTimeout(refreshThreads, 1500);
  }, [refreshThreads]);

  if (!chat) return null;
  return <InboxChatModal item={chat.item} onClose={close} onCloseAfterNavigate={closeInPlace} onNewChat={chat.item.type === 'chat' ? startBlankChat : undefined} />;
}

function OverlayBuild() {
  const build = useOverlayOpenStore((s) => s.build);
  const returnToOrigin = useReturnToOrigin();
  const closeInPlace = useCallback(() => useOverlayOpenStore.getState().clearBuild(), []);
  const close = useCallback(() => {
    const target = useOverlayOpenStore.getState().build?.returnTo ?? null;
    closeInPlace();
    returnToOrigin(target);
  }, [closeInPlace, returnToOrigin]);

  // The Build window closes itself in place when Pro Builder opens.

  if (!build) return null;
  return <CodingSessionModal initialSessionId={build.sessionId} onClose={close} onCloseInPlace={closeInPlace} />;
}
